import { Component } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { AutocompleteDropdownComponent } from "../../../autocomplete-dropdown/autocomplete-dropdown.component";
import { NgClass, NgIf } from "@angular/common";
import { DynamicFormComponent } from "../../../dynamic-form/dynamic-form.component";
import CreditNoteFormConfig from "../config/CrediNoteForm.config.json";
import { DynamicForm, FormRow } from "../../../../models/dynamic-form";
import { DynamicFormService } from "../../../../services/dynamic-form.service";
import { CreditNote, CreditType } from "../../../../models/credit-note";
import { FinanceApiService } from "../../../../services/finance-a-p-iervice.service";
import { LoadingService } from "../../../../services/loading.service";
import { ToasterService } from "../../../../services/toaster.service";
import { ActivatedRoute, Router } from "@angular/router";
import { SvgIconComponent } from "../../../svg-icon/svg-icon.component";
import { PageCloseButtonComponent } from "../../../common/close-button/page-close-button.component";

@Component({
  selector: "app-manage-credit-note",
  standalone: true,
  imports: [
    ReactiveFormsModule,
    AutocompleteDropdownComponent,
    NgClass,
    DynamicFormComponent,
    SvgIconComponent,
    NgIf,
    PageCloseButtonComponent
  ],
  templateUrl: "./manage-credit-note.component.html",
  styleUrl: "./manage-credit-note.component.scss"
})
export class ManageCreditNoteComponent {
  readOnlyMode: boolean = false;
  editingEnabled: boolean = false;
  creditNoteForm!: FormGroup;
  creditNoteFormConfig: DynamicForm = CreditNoteFormConfig;
  invoiceData: any = undefined;
  creditNoteId: string = "";
  goBackUrl = '/manage-finance/credit-notes'

  constructor(private formBuilder: FormBuilder,
              private dynamicFormService: DynamicFormService,
              private financeApiService: FinanceApiService,
              private loadingService: LoadingService,
              private toasterService: ToasterService,
              private router: Router,
              private route: ActivatedRoute) {
    this.creditNoteForm = this.formBuilder.group({});
  }

  ngOnInit(): void {
    this.dynamicFormService.addControlsToForm(this.creditNoteForm, this.creditNoteFormConfig);
    this.creditNoteId = this.route.snapshot.paramMap.get("creditNoteId") || "";
    this.hydrateForm(); //for view mode
    this.createCreditNoteWithInvId();
  }

  goBack() {
    this.router.navigateByUrl(this.goBackUrl);
  }

  save() {
    this.creditNoteForm.markAllAsTouched();
    if (!this.creditNoteForm.valid) {
      return;
    }

    const creditNoteData = {
      ...this.creditNoteForm.value,
      invoiceId: this.creditNoteForm.get("invoiceId")?.value?.key || this.creditNoteForm.get("invoiceId")?.value,
      creditType: CreditType.WAIVER
    };
    this.loadingService.show();
    this.callApiToSaveCreditNote(creditNoteData);
  }

  handleValueFormValueChange(event: any) {
    console.log(event);
    if (event.controlName === "invoiceId") {
      this.invoiceData = event.result;
      this.populateCustomers(this.invoiceData);
      this.populateCharges(this.invoiceData);
    }
  }

  private populateCustomers(invoiceData: any) {
    this.creditNoteFormConfig.formRows.forEach((row: FormRow) => {
      row.formGroups.forEach((group) => {
        if (group.control.label === "customerId" && group.control.fieldInfo) {
          group.control.fieldInfo.options = [
            {
              label: invoiceData?.customer?.name,
              value: invoiceData?.customer?.id
            }
          ];
        }
      });
    });
    setTimeout(() => {
      this.dynamicFormService.setValueToFormControl(this.creditNoteForm, "customerId", invoiceData?.customer?.id);
    });
  }

  private populateCharges(invoiceData: any) {
    this.creditNoteFormConfig.formRows.forEach((row: FormRow) => {
      row.formGroups.forEach((group) => {
        if (group.control.label === "chargeId" && group.control.fieldInfo) {
          group.control.fieldInfo.options = [
            ...(invoiceData?.containerInvoiceItems?.flatMap((item: any) =>
              item.charges?.map((charge: any) => ({
                label: charge?.description,
                value: charge?.chargeId
              })) ?? []
            ) ?? []),

            ...(invoiceData?.items?.map((charge: any) => ({
              label: charge?.description,
              value: charge?.chargeId
            })) ?? []),

            ...[{ label: invoiceData?.charge?.name, value: invoiceData?.chargeId }]

          ];

          console.log(group.control.fieldInfo.options);
        }
      });
    });
  }

  private callApiToSaveCreditNote(creditNoteData: CreditNote) {
    this.financeApiService.createCreditNote(creditNoteData).subscribe({
      next: (response: any) => {
        this.goBack();
        this.toasterService.success("Credit note created successfully");
        this.loadingService.hide();
      },
      error: (error) => {
        this.toasterService.error(error.error.message);
        this.loadingService.hide();

      }
    });
  }

  protected readonly Object = Object;

  private hydrateForm() {
    if(this.creditNoteId === "" && this.route.snapshot.url[0].path !== "view") return;
    this.readOnlyMode = true;
    this.removeConditionsFromFormConfig();
    this.creditNoteForm.disable();
    this.loadingService.show();
    this.financeApiService.getCreditNoteById(this.creditNoteId).subscribe({
      next: (response: any) => {
        this.invoiceData = response.data;
        this.populateCharges(this.invoiceData);
        this.populateCustomers(this.invoiceData);
        setTimeout(() => {
          this.creditNoteForm.patchValue({
            ...this.invoiceData
          });
        })
        this.loadingService.hide();
      },
      error: (error: any) => {
        this.goBack();
        this.loadingService.hide();
        this.toasterService.error(error.error.message);
      }
    });
  }

  private removeConditionsFromFormConfig() {
    this.creditNoteFormConfig.formRows.forEach((row: FormRow) => {
      row.formGroups.forEach((group) => {
        if (group.control?.conditions) {
          group.control.conditions = {};
        }
      });
    });
  }

  private createCreditNoteWithInvId() {
    const creditNoteInputs = history.state?.creditNoteInputs
    if(!creditNoteInputs) return

    const creditNoteInput = creditNoteInputs
    this.goBackUrl = creditNoteInput?.redirectPath || this.goBackUrl
    this.loadingService.show()
    this.financeApiService.getInvoiceDetails(creditNoteInput?.invoiceId).subscribe({
      next: (response: any) => {
        const invoiceRes = response.data
        this.dynamicFormService.setValueToFormControl(this.creditNoteForm, "invoiceId", {key: invoiceRes.id,value: invoiceRes.invoiceNo});
        this.populateCharges(invoiceRes)
        this.populateCustomers(invoiceRes)
        this.loadingService.hide();
      },
      error: (error) => {
        this.toasterService.error(error.error.message);
        this.loadingService.hide();
      }
    })
  }
}
